import React from 'react';
import cs from 'classnames';
import debounce from 'lodash/debounce';
import { Close as SearchCloseIcon, SearchNew as SearchNewIcon } from '../../../Icons/dist';
import { RoutingStore } from '../../stores/RoutingStore';
import { searchRoute } from '../../stores/routes/searchRoute';
import { FedopsLoggerStore } from '../../stores/FedopsLoggerStore';
import { BaseURLStore } from '../../stores/BaseURLStore';
import { HeaderStore } from '../../stores/HeaderStore';
import { ExperimentsStore } from '../../../stores/ExperimentsStore';
import { RouteLocation } from '../../stores/routes/createRoute';
import { injectV2 } from '../../injectV2';
import { ScrollToTopContext } from '../../contexts/ScrollToTopContext';
import { WithTranslations, withTranslations } from '../../../utils/withTranslations';
import { searchTermsToRoutes } from './searchTermsToRoutes';
import { headerSearchDataHooks } from './HeaderSearch.dataHooks';
import styles from './HeaderSearch.scss';

interface SearchProps extends WithTranslations {
  headerStore: HeaderStore;
  routingStore: RoutingStore;
  baseURLStore: BaseURLStore;
  fedopsLoggerStore: FedopsLoggerStore;
  experimentsStore: ExperimentsStore;
  dataHook?: string;
  onSearch?: () => void;
  onClick?: () => void;
  onClear?: () => void;
  focusOnSubTitle?: () => void;
  className?: string;
  setInputRef?: (element?: HTMLInputElement) => void;
}

export class HeaderSearchCmp extends React.Component<SearchProps> {
  private searchInputRef = React.createRef<HTMLInputElement>();
  static contextType = ScrollToTopContext;

  componentDidMount(): void {
    if (this.props.headerStore.isSearchInputFocused) {
      this.searchInputRef.current.focus();
    }
  }

  search = (handleFocus?: boolean) => {
    const { routingStore, headerStore, fedopsLoggerStore, focusOnSubTitle, onSearch, experimentsStore } = this.props;
    const { scrollToTop } = this.context;

    const criteria = headerStore.searchCriteria.trim();
    const isSearchPage = routingStore.matchedRoute.routeName === 'search';
    const isFiltersAppliedToLocation =
      experimentsStore.is('specs.funnel.TemplatesGalleryEnableFilters', 'true') && isSearchPage;
    const { appliedFilters } = routingStore;

    if (criteria) {
      const suggestedCategoryLocation = this.getSuggestedLocation(criteria.trim().toLowerCase());

      if (suggestedCategoryLocation) {
        routingStore.history.push(suggestedCategoryLocation);
      } else {
        fedopsLoggerStore.startSearchInteraction();
        if (isFiltersAppliedToLocation) {
          routingStore.history.push(routingStore.rebuildRoute({ page: 1, criteria, ...appliedFilters }));
        } else {
          routingStore.history.push(routingStore.locationBuilders.search({ criteria }));
        }
      }

      scrollToTop && scrollToTop();
      onSearch && onSearch();

      if (handleFocus) {
        focusOnSubTitle && focusOnSubTitle();
      }
    } else if (this.searchInputRef.current) {
      this.searchInputRef.current.focus();
    }
  };

  debouncedSearch = debounce(this.search, 500);

  private getRouteBySearchTerm(searchTerm: string): string | null {
    if (searchTermsToRoutes[searchTerm]) {
      return searchTermsToRoutes[searchTerm];
    }

    const isCBDSearchRedirectEnabled = this.props.experimentsStore.is(
      'specs.funnel.CBDBannerAndSearchRedirect',
      'true',
    );
    if (isCBDSearchRedirectEnabled && searchTerm.split(' ').includes('cbd')) {
      return 'online-store/cbd';
    }

    return null;
  }

  private getSuggestedCategory(criteria: string): { category: string; subcategory: string | undefined } | null {
    const suggestedRoute = this.getRouteBySearchTerm(criteria);
    if (suggestedRoute) {
      const [category, subcategory] = suggestedRoute.split('/');
      return {
        category,
        subcategory,
      };
    }
    return null;
  }

  private getSuggestedLocation(criteria: string): RouteLocation | null {
    const { routingStore } = this.props;
    const suggestedCategory = this.getSuggestedCategory(criteria);

    if (suggestedCategory) {
      return suggestedCategory.subcategory
        ? routingStore.locationBuilders.subCategory({
            categorySlug: suggestedCategory.category,
            subCategorySlug: suggestedCategory.subcategory,
          })
        : routingStore.locationBuilders.category({ categorySlug: suggestedCategory.category });
    }

    return null;
  }

  private handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const { routingStore, headerStore, onClear } = this.props;
    headerStore.setSearchCriteria('');
    if (this.searchInputRef.current) {
      this.searchInputRef.current.focus();
    }
    onClear && onClear();
    routingStore.history.push(routingStore.locationBuilders.home({}));
  };

  private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.props.headerStore.setSearchCriteria(value);
    if (value.length > 1) {
      this.debouncedSearch();
    }
  };

  private onInputBlur = () => {
    this.props.headerStore.setSearchFocused(false);
  };

  private onInputFocus = () => {
    this.props.headerStore.setSearchFocused(true);
  };

  private onFormSubmit = (event) => {
    event.preventDefault();
    this.debouncedSearch.cancel();
    this.search();
  };

  private onKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.debouncedSearch.cancel();
      this.search(true);
    }
  };

  private setInputRef = (element: HTMLInputElement) => {
    this.props.setInputRef && this.props.setInputRef(element);
    (this.searchInputRef as React.MutableRefObject<HTMLInputElement>).current = element;
  };

  componentWillUnmount() {
    this.debouncedSearch.cancel();
  }

  public render() {
    const { t, baseURLStore, dataHook, headerStore, onClick, className } = this.props;
    const { searchCriteria, isSticky } = headerStore;
    const hasValue = searchCriteria !== '';

    return (
      <form
        role="search"
        data-hook={dataHook}
        className={cs(styles.search, { [styles.sticky]: isSticky }, className)}
        method="GET"
        action={baseURLStore.buildURL(searchRoute.build({ criteria: '' }))}
        onSubmit={this.onFormSubmit}
        onKeyDown={this.onKeyDown}
      >
        <input
          id="criteria"
          name="criteria"
          ref={this.setInputRef}
          className={styles.input}
          autoComplete="off"
          onChange={this.onChange}
          onClick={onClick}
          onBlur={this.onInputBlur}
          onFocus={this.onInputFocus}
          aria-label={t('search.label')}
          data-hook={headerSearchDataHooks.input()}
          data-bi-element="search"
          data-bi-element-value="NULL"
          type="search"
          value={searchCriteria}
          placeholder={t('header.searchPlaceholder')}
        />
        {hasValue ? (
          <button
            onClick={this.handleClear}
            type="button"
            className={styles.buttonClear}
            aria-label={t('search.reset')}
          >
            <SearchCloseIcon className={styles.iconClear} />
          </button>
        ) : (
          <label htmlFor="criteria" className={styles.label}>
            <SearchNewIcon className={styles.iconSearch} />
          </label>
        )}
        <input type="hidden" name="page" value="1" />
      </form>
    );
  }
}

export const HeaderSearch = withTranslations(
  injectV2('routingStore', 'baseURLStore', 'headerStore', 'fedopsLoggerStore', 'experimentsStore')(HeaderSearchCmp),
);
